import type { Capability } from '@fleet/shared';
import type { IconName } from '@/design/icons';

/**
 * The navigation, in the order a counter day runs: what is happening now,
 * what came in, what is booked, then the fleet behind it, then the money.
 *
 * Sections are named after what staff manage — vehicles, bookings, drivers —
 * never after how the system is built.
 */

export interface NavSection {
  readonly path: string;
  readonly label: string;
  readonly icon: IconName;
  /** Omitted means every role sees it. */
  readonly requires?: Capability;
}

export interface NavGroup {
  readonly label: string;
  readonly sections: readonly NavSection[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Counter',
    sections: [
      { path: '/', label: 'Today', icon: 'today' },
      { path: '/enquiries', label: 'Enquiries', icon: 'enquiries' },
      { path: '/bookings', label: 'Bookings', icon: 'bookings' },
      { path: '/calendar', label: 'Calendar', icon: 'calendar' },
      // Under Counter rather than under Fleet: staff reach for this while
      // somebody is on the phone, not when tidying records.
      { path: '/customers', label: 'Customers', icon: 'drivers' },
    ],
  },
  {
    label: 'Fleet',
    sections: [
      { path: '/fleet', label: 'Vehicles', icon: 'fleet' },
      { path: '/drivers', label: 'Drivers', icon: 'drivers' },
      { path: '/rates', label: 'Rates', icon: 'rates', requires: 'rates.edit' },
    ],
  },
  {
    label: 'Money',
    sections: [
      { path: '/invoices', label: 'Invoices', icon: 'invoices' },
      { path: '/dues', label: 'Dues', icon: 'dues', requires: 'reports.dues' },
      { path: '/revenue', label: 'Revenue', icon: 'money', requires: 'reports.revenue' },
    ],
  },
  {
    label: 'Business',
    sections: [
      { path: '/settings', label: 'Settings', icon: 'settings', requires: 'settings.manage' },
      // No `requires`: every account can change its own name and password.
      { path: '/account', label: 'My account', icon: 'settings' },
    ],
  },
];
